import { Component, Inject, OnDestroy, OnInit, HostListener, ElementRef  } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus, IdTokenClaims, InteractionRequiredAuthError, RedirectRequest, PopupRequest } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { Base } from './components/base/base';
import { Industry } from './models/common/industry.model';
import { ResourcePageLink } from './models/resource-page-link/resource-page-link.model';
import { NaicsInformation, User } from './models/user/user.model';
import { EventTrackingService } from './services/event-tracking.service'
import { ExternalLinkService } from './services/external-link.service';

import { b2cPolicies } from '../environments/environment';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string,
  tfp?: string,
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent extends Base implements OnInit, OnDestroy {
  title = 'MEMIC - Safety Director';
  loading: boolean = true;
  jumbotronPage: boolean = true;
  isIframe = false;
  private readonly _destroying$ = new Subject<void>();
  selectedIndustry: string | null = null;
  resourcePages: ResourcePageLink[];
  primaryIndustries: Industry[];
  public searchString: string | null = null;
  showModal = false;
  externalUrl: string | null = null;
  target: string | null = null;
  footerClass = 'footer';
  isLoaded: boolean = false;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private eventTrackingService: EventTrackingService,
    private externalLinkService: ExternalLinkService,
    private el: ElementRef
  ) {
    super();
  }


  private _redirectToLogin() {
    // Redirect to login page. User has cancelled self-asserted or change password.
    let signInFlowRequest: RedirectRequest | PopupRequest = {
      authority: b2cPolicies.authorities.signUpSignIn.authority,
      scopes: [],
      prompt: 'login'
    };
    this.loading = true;
    this.authService.instance.loginRedirect(signInFlowRequest);
  }

  // We need to preserve the Jubotron Page styling
  ngOnInit(): void {



    if (this.isPageRefreshed() !== true) {
      sessionStorage.removeItem('msal.account.keys');
    }

    //start tracking navigation events 
    this.eventTrackingService.startNavigationTracking();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.jumbotronPage = event.url.indexOf('/memic-minutes') === 0
          || event.url.indexOf('/industry-resources') === 0
          || event.url.indexOf('/webinars-on-demand') === 0
          || event.url.indexOf('/dementia-series') === 0 ? true : false
      });

    this.isIframe = window !== window.parent && !window.opener;

    /**
    * You can subscribe to MSAL events as shown below. For more info,
    * visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/events.md
    */
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(async () => {
        this.checkAndSetActiveAccount();
      });


    // This will capture only the LOGIN event and set the user in localStorage
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
          || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
          || msg.eventType === EventType.SSO_SILENT_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
      });

    // Check for cancel
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this._destroying$)
      )
      .subscribe(async (result: EventMessage) => {
        await this.authService.instance.handleRedirectPromise();
        // Check for cancel
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (result.error && result.error.message.indexOf('AADB2C90091') > -1) {
          this._redirectToLogin();
        };
      });
  }


  async checkAndSetActiveAccount() {

    let activeAccount = this.authService.instance.getActiveAccount();
    if (activeAccount) {
      // Set up our user information, we MUST have these claims in order to use SafetyDirector
      let claims: any = activeAccount?.idTokenClaims!;

      // If we don't have our idTokenClaims in cache then refresh them
      // This will cause the MSAL calls to occur again and eventually we will get our claims and move on
      if (claims == null || claims['naicsInformation'] == null) {
        const response = await this.authService.instance.acquireTokenSilent({ scopes: ["openid"] });
        claims = response.account.idTokenClaims
      }

      if (claims && claims['naicsInformation']) {

        let naicsInformation: NaicsInformation = JSON.parse(claims['naicsInformation']);

        let user = new User(
          claims['email'],
          claims['name'],
          claims['given_name'],
          claims['family_name'],
          claims['extension_PolicyNumber'],
          naicsInformation,
          claims['insuredName'],
          claims['businessSegment']
        );
        // Set User details to local storage
        this.setUserDetailsToLocalStorage(user);
        // Set Industry
        let industry = naicsInformation.description;
        this.selectedIndustry = industry;
        this.setIndustry(industry);
        // Finally Show our UI
        this.loading = false;
        this.isLoaded = true;
      } else {
        this._redirectToLogin();
      }
    } else {
      this._redirectToLogin();
    }

  }
  
  public isPageRefreshed(): boolean {
    var isRefreshed = false;
    const [navigationEntry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];

    if (navigationEntry) {
      switch (navigationEntry.type) {
        case 'reload':
          isRefreshed = true;
          break;
        case 'navigate':
          break;
        case 'back_forward':
          break;
        default:
          isRefreshed = false;
      }
    } else {
      console.log('Navigation type information is unavailable.');
    }
    return isRefreshed;
  }

  /*
  @HostListener('document:click', ['$event'])
  handleClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    // Traverse up to find the nearest <a> tag (in case an <img> is clicked)
    const anchor = target.closest('a') as HTMLAnchorElement | null;

    if (!anchor) return; // Not inside an <a>, ignore the click

    const url = anchor.href;
    console.log('AppComp External link clicked:', url); // Debugging

    if (this.externalLinkService.isExternalLink(url)) {
      event.preventDefault(); // Stop the navigation
      this.externalUrl = url;
      this.showModal = true;
    }
  }
  */
  showExternalLinkModalPopup(event: MouseEvent, url: string, target: string) {
      this.externalUrl = url;
      this.target = target;
      this.showModal = true;
  }

  confirmNavigation() {
    this.showModal = false;
    if (this.externalUrl) {
      window.open(this.externalUrl, this.target ?? '_blank');
      this.externalUrl = null;
    }
  }

  cancelNavigation() {
    this.showModal = false;
    this.externalUrl = null;
  }
  
  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
